import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import ReactTable from 'react-table';
import { find, includes } from 'lodash';

import DictionaryToolbar from '../../../AdminTools/components/Dictionary/components/DictionaryToolbar';
import { permissions } from '../../../../common/constants/permissions';
import { isMobile } from '../../../../common/services/isMobile';
import ConfirmationDialog from '../../../../common/components/Form/ConfirmationDialog';
import hasAccess, { addRoleAccessId } from '../../../../common/services/HasAccess';
import { RoleStatusToggle } from '../RoleStatusToggle';
import { fetchRoles, editRole } from '../../actions';
import { isRoleFetching, getRoles } from '../../selector';
import { getUser } from '../../../Login/selectors';
import MappedLabel from '../../../../common/components/MappedLabel/MappedLabel';
import { getColumnClasses } from '../../../../common/components/CommonTableColumns';
import { StatusLabel } from '../../../../common/constants/CommonUsedDropdownsStatics';

export function RolesTable() {
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [inactiveRoleId, setInactivateRoleId] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector(getUser);
  const isFetched = useSelector(isRoleFetching);
  const roles = useSelector(getRoles);

  const hasAddAccess = hasAccess(addRoleAccessId, user.currentPermissions);

  useEffect(() => {
    const rolesFilter = {};

    if (!includes(user.currentPermissions, permissions.FULL_ACCESS)) {
      rolesFilter.exclude_permissions = [permissions.FULL_ACCESS, permissions.SUPERVISOR];
    }

    dispatch(fetchRoles(rolesFilter));
  }, [dispatch, user.currentPermissions]);

  const onRoleStatusToggle = (roleId, checked) => {
    const status = checked ? 'ACTIVE' : 'INACTIVE';

    if (status === 'INACTIVE') {
      setOpenConfirmationDialog(true);
      setInactivateRoleId(roleId);
    } else {
      const { name, permissions } = find(roles, { uuid: roleId });

      dispatch(
        editRole(roleId, {
          name,
          permissions,
          status,
        }),
      );
    }
  };

  const getColumns = () => [
    {
      Header: 'Name',
      id: 'name',
      accessor: 'name',
      ...getColumnClasses({ isLink: true }),
      Cell: ({ original, value }) => (
        <Link to={`/admin/access-levels/${original.uuid}/edit`} className="link">
          {value}
        </Link>
      ),
    },
    {
      Header: 'Status',
      accessor: 'status',
      minWidth: isMobile ? 80 : 150,
      Cell: ({ value }) => <StatusLabel status={value} />,
    },
    {
      accessor: 'status',
      minWidth: 60,
      sortable: false,
      Cell: ({ original, value }) => <RoleStatusToggle roleId={original.uuid} toggled={value === 'ACTIVE'} onToggle={onRoleStatusToggle} />,
    },
  ];

  const onAddClick = () => {
    navigate('/admin/access-levels/add');
  };

  const closeConfirmationDialog = () => {
    setOpenConfirmationDialog(false);
    setInactivateRoleId(null);
  };

  const submitConfirmationDialog = () => {
    const { name, permissions } = find(roles, { uuid: inactiveRoleId });

    dispatch(
      editRole(inactiveRoleId, {
        name,
        permissions,
        status: 'INACTIVE',
      }),
    );

    closeConfirmationDialog();
  };

  return (
    <div className="content-wrapper">
      <div className="list-wrapper">
        <DictionaryToolbar
          title={<MappedLabel reserve="Access Levels">mainMenu/adminTools/accessLevels</MappedLabel>}
          openCreateDialog={hasAddAccess ? onAddClick : undefined}
        />
        <div className="chargers-table">
          <ReactTable
            className="sticky -highlight"
            columns={getColumns()}
            data={roles}
            pageSize={roles.length}
            loading={isFetched}
            defaultSorted={[{ id: 'name', desc: false }]}
            minRows={20}
            showPagination={false}
          />
        </div>
      </div>
      <ConfirmationDialog
        onSubmit={submitConfirmationDialog}
        onCancel={closeConfirmationDialog}
        submitLabel="Submit"
        cancelLabel="Cancel"
        open={openConfirmationDialog}
        bodyText="Are you sure you want to inactivate this role?"
      />
    </div>
  );
}
